/**
 * Hero Section Component
 * Main hero banner with multiple slide images from CMS API
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { isSafeImageUrl } from '../../../utils/isSafeUrl';
import saccosLogo from '../../../assets/logos/saccos_logo.png';
import twentyYears from '../../../assets/images/20years.png';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';

const getAnniversaryContent = async () => {
  const response = await axios.post(
    `${CMS_API_URL}info/by-category`,
    { CategoryId: 62 },
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    }
  );
  return response;
};

const SLIDE_INTERVAL = 5000;

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upcomingEvent, setUpcomingEvent] = useState({ members: '', years: '', image: '', description: '' });
  const timerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isHovered = useRef(false);

  // Fetch slides from CMS API
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}sliders/published`,
          {},
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data)) {
          const apiSlides = response.data.data
            .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))
            .map((item) => ({
              id: item.SliderId,
              image: item.ImageUrl,
              pretitle: item.Title || '',
              title: item.SubTitle || '',
              subtitle: item.Caption || '',
              cta: 'About Us',
              ctaLink: '/about',
            }));

          setSlides(apiSlides);
        }
      } catch (error) {
        console.error('Failed to fetch hero slides:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, []);

  // Fetch anniversary content
  useEffect(() => {
    const loadAnniversaryContent = async () => {
      try {
        const response = await getAnniversaryContent();
        if (response?.data?.code === 1 && Array.isArray(response?.data?.data)) {
          const item = response.data.data
            .filter(i => i.IsDeleted === false && i.IsPublished === true)
            .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))[0];
          if (item) {
            const description = item.Description
              ? item.Description.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
              : '';
            setUpcomingEvent({
              image: item.Image || '',
              members: item.Title || '',
              years: item.SubTitle || '',
              description,
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch anniversary content:', error);
      }
    };

    loadAnniversaryContent();
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % (slides.length || 1));
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + (slides.length || 1)) % (slides.length || 1));
  }, [slides.length]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      if (!isHovered.current) {
        setCurrentSlide((prev) => (prev + 1) % (slides.length || 1));
      }
    }, SLIDE_INTERVAL);
  }, [slides.length]);

  // Autoplay timer
  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
        resetTimer();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
        resetTimer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, resetTimer]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    const diff = touchStartX.current - touchEndX.current;
    const swipeThreshold = 50;
    if (diff > swipeThreshold) {
      nextSlide();
      resetTimer();
    } else if (diff < -swipeThreshold) {
      prevSlide();
      resetTimer();
    }
  };

  const handleMouseEnter = () => {
    isHovered.current = true;
  };

  const handleMouseLeave = () => {
    isHovered.current = false;
  };

  const handlePrevClick = () => {
    prevSlide();
    resetTimer();
  };

  const handleNextClick = () => {
    nextSlide();
    resetTimer();
  };

  const handleDotClick = (index) => {
    goToSlide(index);
    resetTimer();
  };

  if (!loading && slides.length === 0) {
    return null;
  }

  return (
    <section className="relative py-4 md:py-6 overflow-hidden bg-gray-100">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* Banner Container with rounded corners */}
        <div
          className="relative h-[350px] sm:h-[400px] md:h-[450px] lg:h-[500px] rounded-2xl overflow-hidden shadow-xl"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          role="region"
          aria-roledescription="carousel"
          aria-label="Hero Banner Slides"
        >

          {/* Background Images with smooth transitions */}
          {!loading && slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
              aria-hidden={index !== currentSlide}
            >
              {slide.image && isSafeImageUrl(slide.image) ? (
                <div
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${slide.image})` }}
                  role="img"
                  aria-label={slide.title}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#2f3951] to-[#1f2937]" />
              )}
              {/* Light overlay for text readability */}
              <div className="absolute inset-0 bg-black/25"></div>
            </div>
          ))}

          {/* Decorative Elements */}
          <div className="absolute top-10 right-10 w-32 h-32 md:w-48 md:h-48 bg-[#ca9a37]/10 rounded-full blur-3xl z-10 pointer-events-none"></div>
          <div className="absolute bottom-10 left-10 w-40 h-40 md:w-64 md:h-64 bg-blue-500/10 rounded-full blur-3xl z-10 pointer-events-none"></div>

          {/* Content */}
          <div className="relative h-full flex items-center px-4 sm:px-6 md:px-10 lg:px-16 z-20">
            <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center w-full max-w-6xl mx-auto">
              {/* Left Content */}
              <div className="text-white z-10 order-2 md:order-1">
                <div className="mb-3 md:mb-4">
                  <span className="text-[#ca9a37] font-semibold tracking-wider text-xs sm:text-sm uppercase">
                    {slides[currentSlide]?.pretitle || ''}
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 leading-tight">
                  {slides[currentSlide]?.title || ''}
                </h1>
                <p className="text-sm sm:text-base md:text-lg text-gray-200 mb-4 md:mb-6 max-w-md leading-relaxed">
                  {slides[currentSlide]?.subtitle?.replace(/<[^>]*>/g, '')?.replace(/&nbsp;/g, ' ') || ''}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to={slides[currentSlide]?.ctaLink || '/about'}
                    className="inline-flex items-center bg-[#ca9a37] hover:bg-[#b88a2f] text-[#2f3951] font-bold py-2.5 px-5 md:py-3 md:px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#ca9a37] focus:ring-offset-2"
                  >
                    {slides[currentSlide]?.cta || 'About Us'}
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center border-2 border-white/40 hover:border-white text-white font-semibold py-2.5 px-5 md:py-3 md:px-6 rounded-lg transition-all hover:bg-white/10 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-white/50"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>

              {/* Right Content - Logo Circle */}
              <div className="hidden md:flex justify-center items-center order-1 md:order-2">
                {/* <div className="relative">
                  <div className="w-48 h-48 lg:w-64 lg:h-64 bg-gradient-to-br from-[#ca9a37]/20 to-[#b88a2f]/10 rounded-full flex items-center justify-center backdrop-blur-sm border border-[#ca9a37]/30 relative overflow-hidden">
                    <div className="absolute inset-3 lg:inset-4 border-2 border-dashed border-[#ca9a37]/40 rounded-full animate-spin" style={{ animationDuration: '20s' }}></div>
                    <div className="text-center text-white relative z-10">
                      <img
                        src={saccosLogo}
                        // alt="Uhamiaji SACCOS Logo"
                        className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-2 lg:mb-3 object-contain drop-shadow-lg"
                      />
                      <p className="font-bold text-lg lg:text-xl tracking-wide">UHAMIAJI</p>
                      <p className="text-[#ca9a37] text-sm lg:text-base font-medium">SACCOS LIMITED</p>
                      <div className="mt-2 flex items-center justify-center space-x-1">
                        <div className="w-1.5 h-1.5 bg-[#ca9a37] rounded-full"></div>
                        <p className="text-xs text-gray-300">Trusted Financial Partner</p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute -top-2 -left-2 bg-white rounded-lg p-2 shadow-xl">
                    <p className="text-[#2f3951] font-bold text-xs">10K+</p>
                    <p className="text-gray-500 text-[10px]">Members</p>
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-[#ca9a37] rounded-lg p-2 shadow-xl">
                    <p className="text-[#2f3951] font-bold text-xs">18+</p>
                    <p className="text-[#2f3951]/70 text-[10px]">Years</p>
                  </div>
                </div> */}                

                <div className="relative translate-x-0 sm:translate-x-8 md:translate-x-16 lg:translate-x-24 xl:translate-x-32 translate-y-4 sm:translate-y-6 md:translate-y-8 lg:translate-y-10">
                  <div className="w-48 h-48 lg:w-64 lg:h-64 bg-gradient-to-br from-[#ca9a37]/20 to-[#b88a2f]/10 rounded-full flex items-center justify-center backdrop-blur-sm border border-[#ca9a37]/30 relative overflow-hidden">
                    <div
                      className="absolute inset-3 lg:inset-4 border-2 border-dashed border-[#ca9a37]/40 rounded-full animate-spin"
                      style={{ animationDuration: '20s' }}
                    ></div>

                    <div className="text-center text-white relative z-10">
                      <img
                        src={(isSafeImageUrl(upcomingEvent?.image) && upcomingEvent?.image) || twentyYears}
                        alt="20 Years Anniversary"
                        className="w-32 h-32 lg:w-44 lg:h-44 object-contain mx-auto drop-shadow-lg translate-y-1 lg:translate-y-2"
                      />

                      <div className="mt-2 flex items-center justify-center space-x-1">
                        <div className="w-1.5 h-1.5 bg-[#ca9a37] rounded-full"></div>
                        <p className="text-xs text-gray-300">
                          {upcomingEvent?.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -top-2 -left-2 bg-white rounded-lg p-2 shadow-xl">
                    <p className="text-[#2f3951] font-bold text-xs">{upcomingEvent?.members}</p>
                    <p className="text-gray-500 text-[10px]">Members</p>
                  </div>
                  <div className="absolute -bottom-0 -right-2 bg-[#ca9a37] rounded-lg p-2 shadow-xl">
                    <p className="text-[#2f3951] font-bold text-xs">
                      {upcomingEvent?.years ? `${upcomingEvent.years}+` : ''}
                    </p>
                    <p className="text-[#2f3951]/70 text-[10px]">Years</p>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Slide Navigation */}
          <div className="absolute bottom-4 md:bottom-6 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 z-30">
            <button
              type="button"
              onClick={handlePrevClick}
              className="p-2 md:p-2.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
            </button>
            <div className="flex space-x-2" role="tablist" aria-label="Hero slide indicators">
              {slides.map((_, index) => (
                <button
                  type="button"
                  key={index}
                  role="tab"
                  aria-selected={index === currentSlide}
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => handleDotClick(index)}
                  className={`h-1.5 md:h-2 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-white/50 ${index === currentSlide ? 'w-6 md:w-8 bg-[#ca9a37]' : 'w-1.5 md:w-2 bg-white/50 hover:bg-white/70'
                    }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={handleNextClick}
              className="p-2 md:p-2.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-label="Next slide"
            >
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
